import {
  DiagnosticsAnalyticsEvent,
  DiagnosticsBreadcrumb,
  DiagnosticsErrorEntry,
  DiagnosticsSnapshot,
  DeviceSnapshot,
} from './diagnosticsTypes';
import { sanitizeMessage, sanitizeRecord } from './diagnosticsSanitizer';

const MAX_BREADCRUMBS = 60;
const MAX_ERRORS = 30;
const MAX_ANALYTICS_EVENTS = 60;

type MaybeNavigator = Navigator & {
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
  deviceMemory?: number;
};

function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function trimToMax<T>(list: T[], max: number): T[] {
  if (list.length <= max) {
    return list;
  }
  return list.slice(list.length - max);
}

class DiagnosticsCollector {
  private breadcrumbs: DiagnosticsBreadcrumb[] = [];

  private errors: DiagnosticsErrorEntry[] = [];

  private analyticsEvents: DiagnosticsAnalyticsEvent[] = [];

  private listenersInstalled = false;

  addBreadcrumb(category: string, action: string, details?: Record<string, unknown>): void {
    const entry: DiagnosticsBreadcrumb = {
      id: makeId('bc'),
      timestamp: new Date().toISOString(),
      category: sanitizeMessage(category),
      action: sanitizeMessage(action),
      details: details ? sanitizeRecord(details) : undefined,
    };
    this.breadcrumbs = trimToMax([...this.breadcrumbs, entry], MAX_BREADCRUMBS);
  }

  addError(
    source: DiagnosticsErrorEntry['source'],
    message: string,
    options?: {
      category?: string;
      stack?: string;
      retryable?: boolean;
      context?: Record<string, unknown>;
    }
  ): void {
    const entry: DiagnosticsErrorEntry = {
      id: makeId('er'),
      timestamp: new Date().toISOString(),
      source,
      message: sanitizeMessage(message),
      category: options?.category ? sanitizeMessage(options.category) : undefined,
      stack: options?.stack ? sanitizeMessage(options.stack) : undefined,
      retryable: options?.retryable,
      context: options?.context ? sanitizeRecord(options.context) : undefined,
    };
    this.errors = trimToMax([...this.errors, entry], MAX_ERRORS);
  }

  addAnalyticsEvent(
    name: string,
    payload: Record<string, unknown>,
    channel: DiagnosticsAnalyticsEvent['channel']
  ): void {
    const entry: DiagnosticsAnalyticsEvent = {
      id: makeId('an'),
      timestamp: new Date().toISOString(),
      name: sanitizeMessage(name),
      payload: sanitizeRecord(payload),
      channel,
    };
    this.analyticsEvents = trimToMax([...this.analyticsEvents, entry], MAX_ANALYTICS_EVENTS);
  }

  installGlobalErrorListeners(): void {
    if (this.listenersInstalled || typeof window === 'undefined') {
      return;
    }
    window.addEventListener('error', this.handleWindowError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.listenersInstalled = true;
  }

  uninstallGlobalErrorListeners(): void {
    if (!this.listenersInstalled || typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.listenersInstalled = false;
  }

  getSnapshot(): DiagnosticsSnapshot {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    return {
      capturedAt: new Date().toISOString(),
      app: {
        env: process.env.NODE_ENV ?? 'unknown',
        url,
        path,
      },
      device: this.getDeviceSnapshot(),
      breadcrumbs: [...this.breadcrumbs],
      errors: [...this.errors],
      analyticsEvents: [...this.analyticsEvents],
    };
  }

  resetForTests(): void {
    this.breadcrumbs = [];
    this.errors = [];
    this.analyticsEvents = [];
  }

  private readonly handleWindowError = (event: ErrorEvent): void => {
    this.addError('runtime', event.message || 'Unhandled runtime error', {
      stack: event.error instanceof Error ? event.error.stack : undefined,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  };

  private readonly handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    if (reason instanceof Error) {
      this.addError('unhandledrejection', reason.message, {
        stack: reason.stack,
      });
      return;
    }
    this.addError('unhandledrejection', 'Unhandled promise rejection', {
      context: {
        reason: typeof reason === 'string' ? sanitizeMessage(reason) : String(reason),
      },
    });
  };

  private getDeviceSnapshot(): DeviceSnapshot {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {
        userAgent: 'unknown',
      };
    }

    const nav = navigator as MaybeNavigator;
    const perfMemory = (
      performance as Performance & {
        memory?: { jsHeapSizeLimit?: number; totalJSHeapSize?: number; usedJSHeapSize?: number };
      }
    ).memory;

    return {
      userAgent: nav.userAgent,
      language: nav.language,
      languages: Array.isArray(nav.languages) ? nav.languages.slice(0, 6) : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: nav.platform,
      online: nav.onLine,
      cookieEnabled: nav.cookieEnabled,
      screen: {
        width: window.screen?.width,
        height: window.screen?.height,
        pixelRatio: window.devicePixelRatio,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      memory: {
        deviceMemoryGb: nav.deviceMemory,
        jsHeapLimit: perfMemory?.jsHeapSizeLimit,
        totalJsHeapSize: perfMemory?.totalJSHeapSize,
        usedJsHeapSize: perfMemory?.usedJSHeapSize,
      },
      connection: {
        effectiveType: nav.connection?.effectiveType,
        downlinkMbps: nav.connection?.downlink,
        rttMs: nav.connection?.rtt,
        saveData: nav.connection?.saveData,
      },
    };
  }
}

export const diagnosticsCollector = new DiagnosticsCollector();
